import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Plus, Send, Trash2, XCircle } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RelativeTime } from "@/components/RelativeTime";

const SOURCES = ["build", "runtime", "access"] as const;
type Source = typeof SOURCES[number];

const SOURCE_HELP: Record<Source, string> = {
  build: "Build output for every deployment.",
  runtime: "stdout/stderr from running deployments.",
  access: "Caddy access log lines for this project's hosts.",
};

interface LogDrain {
  id: string;
  name: string;
  url: string;
  sources: Source[];
  enabled: boolean;
  failureCount: number;
  lastError: string | null;
  lastStatusCode: number | null;
  lastSuccessAt: string | null;
  createdAt: string;
  hasHeaders: boolean;
  hasSecret: boolean;
}

/**
 * 008 (US4) — log drain configuration.
 *
 * Headers and the signing secret are write-only: the API reports only whether
 * each is set, so this page can show "configured" but never the value.
 */
export function LogDrains() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);

  const base = `/orgs/${org}/projects/${project}/log-drains`;
  const invalidate = () => qc.invalidateQueries({ queryKey: ["log-drains", org, project] });
  const onError = (e: unknown) => setError(e instanceof ApiError ? e.message : "something went wrong");

  const drains = useQuery({
    queryKey: ["log-drains", org, project],
    queryFn: () => api.get<{ drains: LogDrain[] }>(base).then((r) => r.drains),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: () => { setError(null); invalidate(); },
    onError,
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`${base}/${id}`, { enabled }),
    onSuccess: () => { setError(null); invalidate(); },
    onError,
  });
  const test = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; statusCode: number | null; response: string }>(`${base}/${id}/test`),
    onSuccess: (r, id) =>
      setTestResult((p) => ({
        ...p,
        [id]: r.ok ? `OK (${r.statusCode})` : `Failed: ${r.statusCode ?? "no response"} ${r.response}`.trim(),
      })),
    onError,
  });

  if (drains.isLoading) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Log drains</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Forward build, runtime and access logs to an HTTP endpoint. Runtime logs are otherwise
            kept only in a small rolling window per deployment.
          </p>
        </div>
        <Button onClick={() => setAdding((v) => !v)}>
          <Plus className="mr-1 h-4 w-4" /> Add drain
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {adding && (
        <AddDrainForm base={base} onDone={() => { setAdding(false); invalidate(); }} onError={onError} />
      )}

      {drains.data?.length === 0 && !adding && (
        <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          No log drains configured.
        </div>
      )}

      <div className="space-y-3">
        {drains.data?.map((d) => (
          <div key={d.id} className="rounded-md border px-3 py-3">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{d.name}</span>
                  {d.enabled ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600">
                      <CheckCircle2 className="h-3 w-3" /> Enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      <XCircle className="h-3 w-3" /> Disabled
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{d.url}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {d.sources.join(", ")}
                  {d.hasSecret && " · signed"}
                  {d.hasHeaders && " · custom headers"}
                  {d.lastSuccessAt && <> · last delivered <RelativeTime value={d.lastSuccessAt} /></>}
                </p>

                {d.failureCount > 0 && (
                  <p className="mt-1.5 flex items-start gap-1 text-xs text-amber-600">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                      {d.failureCount} consecutive failure{d.failureCount === 1 ? "" : "s"}
                      {d.lastError && <> — {d.lastError}</>}
                      {!d.enabled && " (auto-disabled; re-enable to resume)"}
                    </span>
                  </p>
                )}
                {testResult[d.id] && (
                  <p className="mt-1.5 font-mono text-xs text-muted-foreground">{testResult[d.id]}</p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => test.mutate(d.id)} disabled={test.isPending}>
                  <Send className="mr-1 h-3.5 w-3.5" /> Test
                </Button>
                <Button variant="ghost" size="sm" onClick={() => toggle.mutate({ id: d.id, enabled: !d.enabled })}>
                  {d.enabled ? "Disable" : "Enable"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete ${d.name}`}
                  onClick={() => { if (confirm(`Delete drain "${d.name}"?`)) remove.mutate(d.id); }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddDrainForm({
  base,
  onDone,
  onError,
}: {
  base: string;
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [headers, setHeaders] = useState("");
  const [sources, setSources] = useState<Source[]>(["build"]);
  const [localError, setLocalError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      let parsedHeaders: Record<string, string> | undefined;
      if (headers.trim()) {
        try {
          parsedHeaders = JSON.parse(headers) as Record<string, string>;
        } catch {
          throw new Error('Headers must be a JSON object, e.g. {"authorization":"Bearer …"}');
        }
      }
      return api.post(base, {
        name,
        url,
        sources,
        ...(parsedHeaders ? { headers: parsedHeaders } : {}),
        ...(secret ? { secret } : {}),
      });
    },
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiError) onError(e);
      else setLocalError((e as Error).message);
    },
  });

  return (
    <form
      className="space-y-3 rounded-md border p-4"
      onSubmit={(e) => { e.preventDefault(); setLocalError(null); create.mutate(); }}
    >
      <div className="flex gap-3">
        <div className="w-56">
          <Label htmlFor="drain-name">Name</Label>
          <Input id="drain-name" value={name} placeholder="Axiom" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex-1">
          <Label htmlFor="drain-url">Endpoint URL</Label>
          <Input
            id="drain-url"
            value={url}
            placeholder="https://api.axiom.co/v1/datasets/logs/ingest"
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Sources</Label>
        <div className="mt-1 flex flex-wrap gap-4">
          {SOURCES.map((s) => (
            <label key={s} className="flex items-start gap-1.5 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={sources.includes(s)}
                onChange={(e) =>
                  setSources((prev) => (e.target.checked ? [...prev, s] : prev.filter((x) => x !== s)))
                }
              />
              <span>
                {s}
                <span className="block text-xs text-muted-foreground">{SOURCE_HELP[s]}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <Label htmlFor="drain-headers">Custom headers (JSON, optional)</Label>
          <Input
            id="drain-headers"
            value={headers}
            placeholder={'{"authorization":"Bearer …"}'}
            onChange={(e) => setHeaders(e.target.value)}
          />
        </div>
        <div className="w-64">
          <Label htmlFor="drain-secret">Signing secret (optional)</Label>
          <Input id="drain-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} />
        </div>
      </div>

      {localError && <p className="text-sm text-destructive">{localError}</p>}

      <div className="flex justify-end">
        <Button type="submit" disabled={!name || !url || sources.length === 0 || create.isPending}>
          Create drain
        </Button>
      </div>
    </form>
  );
}
